import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { HorizontalLayout } from 'widgets/Layout';

import { FormElement, FormLabel } from '../FormCommons';
import { Box, Checkbox, CheckInput } from './CheckboxCommons';

interface FormCheckboxGroupProps {
    name: string;
    disabled?: boolean;
    options: { label: string; value: string }[];
    label?: string;
}

export const FormCheckboxGroup = ({ name, disabled = false, options, label }: FormCheckboxGroupProps) => {
    const { register, watch, setValue } = useFormContext();

    register(name, { value: [] });
    const selectedValues: string[] = watch(name, []);

    const onChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = target.checked
            ? [...selectedValues, target.value]
            : selectedValues.filter((val) => val !== target.value);
        setValue(name, newValue, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    const Grid = (
        <CheckboxGrid>
            {options.map((option) => {
                const checked = selectedValues.includes(option.value);
                return (
                    <CheckboxGroupItem $checked={checked} key={option.value} $rowCenter>
                        <Checkbox>
                            <CheckInput
                                id={`${name}-${option.value}`}
                                type="checkbox"
                                disabled={disabled}
                                onChange={onChange}
                                defaultValue={option.value}
                                checked={checked}
                            />
                            <Box $checked={checked}>{checked && '✔'}</Box>
                            <FormLabel as="span">{option.label}</FormLabel>
                        </Checkbox>
                    </CheckboxGroupItem>
                );
            })}
        </CheckboxGrid>
    );

    return label ? (
        <FormElement>
            <FormLabel as="span">{label}</FormLabel>
            {Grid}
        </FormElement>
    ) : (
        Grid
    );
};

const CheckboxGroupItem = styled(HorizontalLayout)<{ $checked: boolean }>`
    border: 1px solid ${({ theme, $checked }) => ($checked ? theme.border.dark : theme.border.light)};
    border-radius: 3px;
    padding: 8px;
`;

const CheckboxGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 8px;
    column-gap: 8px;
`;
